import { useSyncExternalStore } from 'react';

/**
 * Modo discreto da clínica: quando um paciente chega perto da tela, um toque esconde o que
 * é sensível (valores, nome completo, telefone, origem, números clínicos) e deixa o que
 * a recepção precisa para trabalhar (horário, primeiro nome, tipo de consulta, situação).
 * É de cada aparelho e fica ligado até alguém desligar. A raiz do app escuta e redesenha
 * a tela sem perder o que estava aberto (formulários, folhas).
 */
const KEY = 'sec.clinica.discreto';
const listeners = new Set<() => void>();
let on = read();

function read(): boolean {
  try {
    return localStorage.getItem(KEY) === '1';
  } catch {
    return false;
  }
}
function paint() {
  if (typeof document === 'undefined') return;
  // A marca no <html> só serve ao CSS da clínica (.ck, .mesa), então pode ficar sempre.
  if (on) document.documentElement.dataset.discreet = 'on';
  else delete document.documentElement.dataset.discreet;
}
paint();

/** Só vale dentro da clínica (e na página pública dela não): o Pessoal mostra tudo sempre. */
export function isDiscreet(): boolean {
  return (
    on && typeof window !== 'undefined' && window.location.pathname.startsWith('/negocio/clinica')
  );
}
/** A escolha guardada (para o botão), mesmo fora da clínica. */
export function discreetChoice(): boolean {
  return on;
}
export function setDiscreet(value: boolean) {
  on = value;
  try {
    if (value) localStorage.setItem(KEY, '1');
    else localStorage.removeItem(KEY);
  } catch {
    // Sem armazenamento: vale até fechar.
  }
  paint();
  for (const listener of listeners) listener();
}
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
export function useDiscreet(): boolean {
  return useSyncExternalStore(subscribe, discreetChoice, () => false);
}

export const HIDDEN = '•••';

/** "Ana Paula Ribeiro" vira "Ana R." no modo discreto. */
export function who(name: string | null | undefined): string {
  const full = (name ?? '').trim();
  if (!isDiscreet() || !full) return full;
  const parts = full.split(/\s+/);
  if (parts.length === 1) return parts[0]!;
  return `${parts[0]} ${parts.at(-1)![0]!.toUpperCase()}.`;
}

/** Telefone com só os 4 últimos números no modo discreto. */
export function phoneShown(phone: string | null | undefined): string {
  const value = (phone ?? '').trim();
  if (!isDiscreet() || !value) return value;
  const digits = value.replace(/\D/g, '');
  return digits.length >= 4 ? `${HIDDEN} ${digits.slice(-4)}` : HIDDEN;
}

/** Qualquer outro dado sensível (origem, CPF, e-mail, número clínico): some no modo discreto. */
export function sensitive(value: string | null | undefined): string {
  if (!isDiscreet()) return value ?? '';
  // Por extenso: três pontos numa coluna de texto parecem o botão de menu.
  return value ? 'oculto' : '';
}
